import React, { useState, useEffect, useMemo } from 'react';
import moduleService from '../api/moduleService';
import authService from '../api/authService';
import { CATEGORY_OPTIONS, getCategoryLabel, getCategoryColor } from '../constants/moduleCategories';
import './ManagementPages.css';
import './Modal.css';

const ModuleManagement = () => {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [categoryFilter, setCategoryFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Define the full structure for the form's state
    // Note: field names must match backend model (name, duration, module_code, description, category)
    const initialFormState = {
        id: null,
        name: '',
        module_code: '',
        description: '',
        duration: '',
        category: ''
    };
    const [formData, setFormData] = useState(initialFormState);
    
    // Role-based security check
    const currentUser = authService.getCurrentUser();
    const isScheduler = currentUser && currentUser.role === 'scheduler';

    // Function to fetch all modules from the API
    useEffect(() => {
        setLoading(true);
        moduleService.getAllModules()
          .then(res => setModules(res.data))
          .catch(err => console.error('Error fetching modules:', err))
          .finally(() => setLoading(false));
    }, []);

    // Handler to update form state as the user types
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors({ ...fieldErrors, [name]: '' });
        }
    };

    // Modal handlers
    const handleOpenAddModal = () => {
        setEditingModule(null);
        setFormData(initialFormState);
        setShowModal(true);
    };

    const handleOpenEditModal = (module) => {
        setEditingModule(module);
        setFormData({
          name: module.name || '',
          module_code: module.module_code || '',
          description: module.description || '',
          duration: module.duration || '',
          category: module.category || ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingModule(null);
        setFormData(initialFormState);
        setFieldErrors({});
    };

    // Parse error response and extract field-level errors
    const parseErrors = (error) => {
        const errors = {};
        const response = error.response?.data;

        if (response?.errors && Array.isArray(response.errors)) {
            // Handle express-validator style errors
            response.errors.forEach(err => {
                const field = err.path || err.param || err.field;
                if (field) {
                    errors[field] = err.msg || err.message;
                }
            });
        } else if (response?.message) {
            // Try to extract field name from message
            const msg = response.message.toLowerCase();
            if (msg.includes('name')) {
                errors.name = response.message;
            } else if (msg.includes('module_code') || msg.includes('code')) {
                errors.module_code = response.message;
            } else if (msg.includes('duration')) {
                errors.duration = response.message;
            } else if (msg.includes('category')) {
                errors.category = response.message;
            } else {
                errors._general = response.message;
            }
        } else {
            errors._general = 'Could not save module. Please check your input.';
        }
        return errors;
    };

    // Handles both creating a new module and updating an existing one
    const handleSubmit = (e) => {
        e.preventDefault();
        setFieldErrors({});

        const action = editingModule
          ? moduleService.updateModule(editingModule.id, formData)
          : moduleService.createModule(formData);

        action.then(() => {
          moduleService.getAllModules().then(res => setModules(res.data));
          handleCloseModal();
        }).catch(err => {
          const errors = parseErrors(err);
          setFieldErrors(errors);
        });
    };

    // Handles the deletion of a module
    const handleDelete = (id) => {
        if (window.confirm('🗑️ Are you sure you want to delete this module? This action cannot be undone.')) {
          moduleService.deleteModule(id).then(() => {
            moduleService.getAllModules().then(res => setModules(res.data));
            alert('✅ Module deleted successfully!');
          }).catch(err => {
            alert(`❌ Error deleting module: ${err.response?.data?.message || err.message}`);
          });
        }
    };
  
    // Filter modules based on category and search term
    const filteredModules = useMemo(() => {
        return modules.filter(module => {
            const matchesCategory = !categoryFilter || module.category === categoryFilter;
            const matchesSearch = !searchTerm ||
                module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                module.module_code.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [modules, categoryFilter, searchTerm]);

    // Calculate stats for compact header
    const totalModules = filteredModules.length;
    const totalHours = filteredModules.reduce((sum, m) => sum + (parseInt(m.duration) || 0), 0);

    return (
        <div className="management-page">
            {/* Compact Header with inline stats and Add button */}
            <div className="page-header-compact">
                <div className="header-left">
                    <h1 className="page-title">
                        <span className="page-title-icon">🎓</span> Module Management
                    </h1>
                    <div className="inline-stats">
                        <span className="stat-item">📊 {totalModules} Modules</span>
                        <span className="stat-item">⏰ {totalHours} Hours</span>
                    </div>
                </div>
                {isScheduler && (
                    <button className="add-btn-primary" onClick={handleOpenAddModal}>
                        + Add Module
                    </button>
                )}
            </div>

            {/* Filter Bar */}
            <div className="filter-bar fade-in">
                <div className="search-field">
                    <input
                        type="text"
                        placeholder="🔍 Search modules..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-field">
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="role-filter"
                    >
                        <option value="">All Categories</option>
                        {CATEGORY_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.value} - {getCategoryLabel(option.value)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-spinner fade-in">
                    <div className="spinner"></div>
                    <span>Loading module data...</span>
                </div>
            ) : (
                <table className="management-table fade-in">
                    <thead>
                        <tr>
                            <th>Module Name</th>
                            <th>Code</th>
                            <th>Category</th>
                            <th>Duration (hrs)</th>
                            <th>Description</th>
                            {isScheduler && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredModules.map(module => (
                            <tr key={module.id}>
                                <td>
                                    <strong>{module.name}</strong>
                                </td>
                                <td>
                                    <span className="module-code">{module.module_code}</span>
                                </td>
                                <td>
                                    {module.category ? (
                                        <span
                                            className="category-badge"
                                            style={{ backgroundColor: getCategoryColor(module.category) }}
                                            title={getCategoryLabel(module.category)}
                                        >
                                            {module.category}
                                        </span>
                                    ) : (
                                        <span className="no-category">N/A</span>
                                    )}
                                </td>
                                <td>
                                    <span className="duration-badge">{module.duration}h</span>
                                </td>
                                <td className="description-cell">
                                    {module.description ? module.description.substring(0, 100) + '...' : 'N/A'}
                                </td>
                                {isScheduler && (
                                    <td className="actions-cell">
                                        <button
                                            onClick={() => handleOpenEditModal(module)}
                                            className="action-button edit"
                                            title="Edit module"
                                        >
                                            ✏️ Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(module.id)}
                                            className="action-button delete"
                                            title="Delete module"
                                        >
                                            🗑️ Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Modal Dialog */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingModule ? '✏️ Edit Course Module' : '🎓 Add New Course Module'}</h3>
                            <button className="modal-close" onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit} className="modal-form">
                            {fieldErrors._general && (
                                <div className="form-error-banner">
                                    ⚠️ {fieldErrors._general}
                                </div>
                            )}
                            <div className="form-grid">
                                <div className={`form-field ${fieldErrors.name ? 'has-error' : ''}`}>
                                    <label>📚 Module Name</label>
                                    <input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Digital Marketing Fundamentals"
                                        required
                                        className={fieldErrors.name ? 'input-error' : ''}
                                    />
                                    {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                                </div>

                                <div className={`form-field ${fieldErrors.module_code ? 'has-error' : ''}`}>
                                    <label>🔖 Module Code</label>
                                    <input
                                        name="module_code"
                                        value={formData.module_code}
                                        onChange={handleInputChange}
                                        placeholder="e.g., DMF-101"
                                        required
                                        className={fieldErrors.module_code ? 'input-error' : ''}
                                    />
                                    {fieldErrors.module_code && <span className="field-error">{fieldErrors.module_code}</span>}
                                </div>

                                <div className={`form-field ${fieldErrors.duration ? 'has-error' : ''}`}>
                                    <label>⏰ Duration (Hours)</label>
                                    <input
                                        name="duration"
                                        type="number"
                                        value={formData.duration}
                                        onChange={handleInputChange}
                                        placeholder="Training hours"
                                        className={fieldErrors.duration ? 'input-error' : ''}
                                    />
                                    {fieldErrors.duration && <span className="field-error">{fieldErrors.duration}</span>}
                                </div>

                                <div className={`form-field ${fieldErrors.category ? 'has-error' : ''}`}>
                                    <label>🏷️ Category</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className={fieldErrors.category ? 'input-error' : ''}
                                    >
                                        <option value="">-- Select Category --</option>
                                        {CATEGORY_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.value} - {getCategoryLabel(option.value)}
                                            </option>
                                        ))}
                                    </select>
                                    {fieldErrors.category && <span className="field-error">{fieldErrors.category}</span>}
                                </div>

                                <div className="form-field full-width">
                                    <label>📝 Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Module description and overview"
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                                    ❌ Cancel
                                </button>
                                <button type="submit" className="submit-btn">
                                    {editingModule ? '💾 Save Changes' : '➕ Create Module'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModuleManagement;